import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './Cloud.css';
import { useConfig } from '../ConfigContext';

// Reusable icon placeholder — consistent size, teal/blue gradient ring
function IconPlaceholder({ size = 40, label = '' }) {
  return (
    <div
      className="cl-icon-placeholder"
      style={{ width: size, height: size, minWidth: size }}
      aria-label={label}
    >
      <span className="cl-icon-ph-label">{label}</span>
    </div>
  );
}

function Cloud() {
  const config = useConfig();
  const cl = config.cloudPage ?? {};
  const showPH = cl.showPlaceholders ?? true;
  const clHeroEnabled        = cl.heroEnabled        !== false;
  const clArchEnabled        = cl.archEnabled        !== false;
  const clApiFeaturesEnabled = cl.apiFeaturesEnabled !== false;
  const clPlatformsEnabled   = cl.platformsEnabled   !== false;
  const clComparisonEnabled  = cl.comparisonEnabled  !== false;
  const clCTAEnabled         = cl.ctaEnabled         !== false;
  const [expandedPlatform, setExpandedPlatform] = useState(null);

  const platforms = (cl.platforms ?? []).length > 0
    ? cl.platforms.map((p, i) => ({
        id: `platform-${i}`,
        name: p.name,
        status: 'available',
        modeLabel: 'Cloud or Offline',
        iconLabel: p.name,
        icon: p.icon ?? null,
        description: p.desc,
        specs: p.specs ?? [],
        accent: i % 2 === 0 ? 'teal' : 'blue',
      }))
    : [
    {
      id: 'web',
      icon: '/icons/ondevice/chrome.png',
      name: 'Web App',
      status: 'available',
      modeLabel: 'Cloud',
      iconLabel: 'Web',
      description: 'Browser-based clients stream audio to your backend via the REST API. No SDK installation required on the client side — works in any modern browser with microphone access.',
      specs: [
        'REST API — fetch or axios from any JS framework',
        'Live microphone streaming via MediaRecorder',
        'Audio file upload support',
        'CORS configurable per deployment',
        'Works in Chrome, Firefox, Safari, Edge',
      ],
      accent: 'teal',
    },
    {
      id: 'android',
      icon: '/icons/ondevice/android.png',
      name: 'Android',
      status: 'available',
      modeLabel: 'Cloud or Offline',
      iconLabel: 'Android',
      description: 'The Kotlin SDK connects to your cloud backend when OFFLINE is not set. Larger model variants run server-side — the client handles audio capture and VAD on-device, inference happens in the cloud.',
      specs: [
        'Native Kotlin SDK — cloud mode out of the box',
        'On-device VAD — only speech reaches the backend',
        'Switchable to offline with OFFLINE=true',
        'Supports ARMv8 and x86_64 architectures',
        'Background service and foreground mic support',
      ],
      accent: 'blue',
    },
    {
      id: 'windows',
      icon: '/icons/ondevice/windows.png',
      name: 'Windows',
      status: 'available',
      modeLabel: 'Cloud or Offline',
      iconLabel: 'Windows',
      description: 'Python SDK defaults to cloud-backed inference when OFFLINE is not set. Centralized model management — update models on your backend without touching client deployments.',
      specs: [
        'Python SDK — pip install tytonvoice',
        'Cloud mode for larger, higher-accuracy models',
        'Switchable to offline with OFFLINE=true',
        'Windows 10 and 11 supported',
        'Live mic and audio file input',
      ],
      accent: 'teal',
    },
    {
      id: 'linux',
      icon: '/icons/ondevice/linux.png',
      name: 'Linux',
      status: 'available',
      modeLabel: 'Cloud or Offline',
      iconLabel: 'Linux',
      description: 'The primary server and edge-node platform. Run the backend itself on Linux, or deploy the client SDK in cloud mode pointing at a separate inference server.',
      specs: [
        'Python SDK — pip install tytonvoice',
        'Host the backend on the same or separate server',
        'Tested on Ubuntu, Debian, and RHEL',
        'x86_64 and ARM64 architectures',
        'Systemd service integration supported',
      ],
      accent: 'blue',
    },
  ];

  const apiFeatures = [
    { iconLabel: 'Init',   title: 'Keyword Initialization',   desc: 'POST /api/initialize — define your keyword vocabulary in plain text. Labels take effect immediately, no redeployment.' },
    { iconLabel: 'Detect', title: 'Detection Endpoint',       desc: 'POST /api/transcribe — send audio, receive detection result, per-label confidence scores, and the top matching keyword.' },
    { iconLabel: 'STT',    title: 'Open Transcription',       desc: 'POST /api/transcribe-only — full speech-to-text without a keyword vocabulary. Same pipeline, different output mode.' },
    { iconLabel: 'WS',     title: 'Live Config via WebSocket', desc: 'Config changes propagate to all connected clients instantly via WebSocket — no page reload, no redeployment.' },
    { iconLabel: 'Auth',   title: 'Token Authentication',     desc: 'Bearer token auth on all sensitive endpoints. Configurable expiry, bcrypt-hashed admin credentials.' },
    { iconLabel: 'Rate',   title: 'Rate Limiting',            desc: 'Per-endpoint rate limits protect inference resources. Configurable via Flask-Limiter with memory or Redis storage.' },
  ];

  const comparisonRows = [
    { feature: 'Model runs on',        cloud: 'Your backend server',    onDevice: 'End-user hardware' },
    { feature: 'Client requirement',   cloud: 'Audio capture only',     onDevice: 'Full SDK + model weights' },
    { feature: 'Model updates',        cloud: 'Backend only, instant',  onDevice: 'Per-device redeployment' },
    { feature: 'Network dependency',   cloud: 'Required at runtime',    onDevice: 'None with OFFLINE=true' },
    { feature: 'Audio privacy',        cloud: 'Your server, your rules',onDevice: 'Never transmitted' },
    { feature: 'Model size',           cloud: 'Unconstrained',          onDevice: 'Hardware-limited' },
    { feature: 'Latency',              cloud: 'Network + inference',    onDevice: 'Inference only' },
    { feature: 'Concurrent users',     cloud: 'Server-capacity limited',onDevice: 'Per-device independent' },
  ];

  return (
    <div className="cl-page">

      {/* ── HERO ── */}
      {clHeroEnabled && <section className="cl-hero">
        <div className="cl-hero-left">
          <div className="cl-hero-badge">Cloud Deployment</div>
          <h1 className="cl-hero-title">
            {cl.heroTitle
              ? cl.heroTitle.split('\n').map((line, i, arr) =>
                  i < arr.length - 1
                    ? <React.Fragment key={i}>{line}<br /></React.Fragment>
                    : <React.Fragment key={i}><span className="cl-hero-title-accent">{line}</span></React.Fragment>
                )
              : (<>Voice Intelligence.<br />Your Infrastructure.<br /><span className="cl-hero-title-accent">Your Data.</span></>)
            }
          </h1>
          <p className="cl-hero-subtitle">
            {cl.heroSubtitle ?? 'Deploy the Tytonvoice backend on any cloud provider or server. Clients send audio — your infrastructure runs inference. No audio ever reaches Tytonvoice servers.'}
          </p>
          <div className="cl-hero-cta-group">
            <a
              href={config.navbar.contactUs.url}
              target="_blank"
              rel="noopener noreferrer"
              className="cl-btn-primary"
            >
              Contact Us
            </a>
            <Link to="/pryme#try-pryme-live" className="cl-btn-secondary">
              Try Live Demo
            </Link>
          </div>
          <div className="cl-hero-stats">
            {(cl.heroStats ?? [
              { value: '4',         label: 'Client platforms supported' },
              { value: 'REST + WS', label: 'API protocols' },
              { value: '100%',      label: 'Your infrastructure' },
            ]).map((stat, i) => (
              <React.Fragment key={i}>
                {i > 0 && <div className="cl-stat-divider" />}
                <div className="cl-stat">
                  <span className="cl-stat-value">{stat.value}</span>
                  <span className="cl-stat-label">{stat.label}</span>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
        <div className="cl-hero-right">
          <div className={cl.heroImage ? "cl-hero-visual cl-hero-visual-img" : "cl-hero-visual"}>
            {cl.heroImage
              ? <img src={cl.heroImage} alt="Cloud Hero" style={{ width:'100%', height:'100%', objectFit:'cover', borderRadius:'16px', opacity: 0.15 }} />
              : showPH && (
                <>
                  <span className="cl-ph-label">Hero Visual</span>
                  <span className="cl-ph-sub">Cloud architecture diagram — client → your backend → response</span>
                </>
              )
            }
          </div>
        </div>
      </section>}

      {/* ── PROBLEM BAND ── */}
      {clHeroEnabled && <section className="cl-problem">
        <div className="cl-problem-inner">
          <p className="cl-problem-text">
            Managed voice AI services route your audio through third-party infrastructure
            by default — with opaque data retention, per-request billing, and no control
            over where inference runs.{' '}
            <strong>Tytonvoice cloud deployment means your backend, your network path, your rules.</strong>
          </p>
        </div>
      </section>}

      {/* ── ARCHITECTURE VISUAL ── */}
      {clArchEnabled && <section className="cl-architecture">
        <div className="cl-architecture-inner">
          <div className="cl-section-header">
            <h2>How It Works</h2>
            <p className="cl-section-subtitle">
              A lightweight REST API sits in front of the Tytonvoice inference engine —
              clients send audio, your backend returns results
            </p>
          </div>
          <div className="cl-arch-flow">
            {[
              { iconLabel: 'Client', title: 'Client',        desc: 'Web, Android, Windows, or Linux captures audio on-device' },
              { iconLabel: 'VAD',    title: 'On-Device VAD', desc: 'Voice activity detection runs locally — silence is never transmitted' },
              { iconLabel: 'API',    title: 'Your API',      desc: 'Speech segment sent to your backend via REST' },
              { iconLabel: 'Engine', title: 'Inference',     desc: 'Tytonvoice pipeline runs on your server — detection or transcription' },
              { iconLabel: 'Result', title: 'Result',        desc: 'Confidence scores and transcript returned to client' },
            ].map((step, i, arr) => (
              <React.Fragment key={i}>
                <div className="cl-arch-step">
                  <div className="cl-arch-step-title">{step.title}</div>
                  <div className="cl-arch-step-desc">{step.desc}</div>
                </div>
                {i < arr.length - 1 && <div className="cl-arch-arrow">→</div>}
              </React.Fragment>
            ))}
          </div>
          <div className="cl-arch-note">
            VAD runs on the client in all SDK modes — only speech segments reach your backend,
            keeping bandwidth and inference cost low.
          </div>
        </div>
      </section>}

      {/* ── API FEATURES ── */}
      {clApiFeaturesEnabled && <section className="cl-api-features">
        <div className="cl-section-header">
          <h2>REST API</h2>
          <p className="cl-section-subtitle">
            Six endpoints covering initialization, detection, transcription, and live configuration
          </p>
        </div>
        <div className="cl-api-grid">
          {apiFeatures.map((f, i) => (
            <div key={i} className={`cl-api-card cl-api-${i % 2 === 0 ? 'teal' : 'blue'}`}>
              <h3 className="cl-api-title">{f.title}</h3>
              <p className="cl-api-desc">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>}

      {/* ── PLATFORM GRID ── */}
      {clPlatformsEnabled && <section className="cl-platforms">
        <div className="cl-section-header">
          <h2>Supported Client Platforms</h2>
          <p className="cl-section-subtitle">
            Any platform that can make an HTTP request can be a Tytonvoice client
          </p>
        </div>
        <div className="cl-platform-grid">
          {platforms.map((p) => (
            <div
              key={p.id}
              className={`cl-platform-card cl-platform-${p.accent} ${expandedPlatform === p.id ? 'expanded' : ''}`}
              onClick={() => setExpandedPlatform(expandedPlatform === p.id ? null : p.id)}
            >
              <div className="cl-platform-top">
                {p.icon
                  ? <img src={p.icon} alt={p.name} className="cl-platform-icon" />
                  : <IconPlaceholder size={38} label={p.iconLabel} />
                }
                <div className="cl-platform-title-group">
                  <h3 className="cl-platform-name">{p.name}</h3>
                  <span className="cl-mode-badge">{p.modeLabel}</span>
                </div>
                <span className="cl-platform-caret">{expandedPlatform === p.id ? '▲' : '▼'}</span>
              </div>
              <p className="cl-platform-desc">{p.description}</p>
              {expandedPlatform === p.id && (
                <ul className="cl-platform-specs">
                  {p.specs.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              )}
            </div>
          ))}
        </div>
      </section>}

      {/* ── INFRASTRUCTURE ── */}


      {/* ── CLOUD vs ON-DEVICE ── */}
      {clComparisonEnabled && <section className="cl-comparison">
        <div className="cl-section-header">
          <h2>Cloud vs. On-Device</h2>
          <p className="cl-section-subtitle">
            Same SDK API, same detection accuracy — different execution environment
          </p>
        </div>
        {/* Desktop */}
        <div className="cl-comparison-table cl-comp-desktop-only">
          <div className="cl-comparison-row cl-comparison-header">
            <div className="cl-comparison-cell cl-cell-feature">Characteristic</div>
            <div className="cl-comparison-cell cl-cell-cloud">Cloud Deployment</div>
            <div className="cl-comparison-cell cl-cell-device">On-Device</div>
          </div>
          {comparisonRows.map((row, i) => (
            <div key={i} className="cl-comparison-row">
              <div className="cl-comparison-cell cl-cell-feature">{row.feature}</div>
              <div className="cl-comparison-cell cl-cell-cloud cl-cell-highlight-cloud">{row.cloud}</div>
              <div className="cl-comparison-cell cl-cell-device">{row.onDevice}</div>
            </div>
          ))}
        </div>
        {/* Mobile — stacked cards */}
        <div className="cl-comp-mobile">
          <div className="cl-comp-mobile-col-headers">
            <div className="cl-comp-mobile-col-label cl-comp-mobile-col-cloud">Cloud Deployment</div>
            <div className="cl-comp-mobile-col-label">On-Device</div>
          </div>
          {comparisonRows.map((row, i) => (
            <div key={i} className="cl-comp-mobile-card">
              <div className="cl-comp-mobile-feature">{row.feature}</div>
              <div className="cl-comp-mobile-values">
                <div className="cl-comp-mobile-val cl-comp-mobile-val-cloud">{row.cloud}</div>
                <div className="cl-comp-mobile-val">{row.onDevice}</div>
              </div>
            </div>
          ))}
        </div>
        <p className="cl-comparison-note">
          Both modes use the same SDK API — switching between cloud and on-device requires
          only a configuration change, not a code change.
        </p>
      </section>}

      {/* ── CTA ── */}
      {clCTAEnabled && <section className="cl-cta-section">
        <div className="cl-cta-split">
          <div className="cl-cta-left">
            <p className="cl-cta-eyebrow">Deploy on your infrastructure</p>
            <div className="cl-cta-heading">
              <div className="cl-cta-brand">Cloud</div>
              <div className="cl-cta-descriptor">Voice Intelligence</div>
            </div>
            <p className="cl-cta-body">
              {cl.ctaBody ?? 'Your backend, your data, your network path. Deploy the Tytonvoice API on any server or cloud provider and connect any client platform in minutes.'}
            </p>
            <a
              href={config.navbar.contactUs.url}
              target="_blank"
              rel="noopener noreferrer"
              className="cl-btn-primary"
            >
              Contact Us
            </a>
          </div>
          <div className="cl-cta-right">
            <ul className="cl-cta-checklist">
              {(cl.ctaChecklist ?? [
                'Deploy on any cloud provider or VPS',
                'Audio never reaches Tytonvoice servers',
                'Web, Android, Windows, and Linux clients',
                'REST API + live WebSocket config',
                'Same API as on-device — one codebase',
              ]).map((item, i) => (
                <li key={i} className="cl-cta-check-item">
                  <span className="cl-cta-check-icon">✓</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>}

    </div>
  );
}

export default Cloud;